/**
 * Projects component - Horizontal flex with 2-3 columns:
 * - Project name (always)
 * - Version badge (only if more than one version)
 * - Chevron (points up)
 */

import { createChevron, type ChevronAPI } from "@UI/Chevron/index.js";
import { createIcon, type IconAPI } from "@UI/Icon/index.js";
import "./Projects.scss";

export interface ProjectsConfig {
  readonly projectName?: string;
  readonly versionLabel?: string; // e.g., "v2" or "2.0"
  readonly totalVersions?: number; // show version badge only if > 1
  readonly onClick?: () => void; // click on the whole container
  readonly onChevronClick?: (anchorEl: HTMLElement) => void;
  readonly icon?: ProjectIconName;
}

export interface ProjectsAPI {
  readonly getElement: () => HTMLElement;
  readonly setProjectName: (name: string) => void;
  readonly setVersion: (label: string | null, totalVersions?: number) => void;
  readonly setIcon: (icon: ProjectIconName) => void;
  readonly destroy: () => void;
}

export type ProjectIconName =
  | "project_web"
  | "project_dashboard"
  | "project_app";

export const createProjects = (config: ProjectsConfig = {}): ProjectsAPI => {
  const {
    projectName = "Untitled",
    versionLabel = null,
    totalVersions = 1,
    onClick,
    onChevronClick,
    icon = "project_web",
  } = config;

  let isDestroyed = false;
  let currentProjectName: string = projectName;
  let currentVersionLabel: string | null = versionLabel;
  let currentTotalVersions: number = totalVersions;
  let chevronAPI: ChevronAPI | null = null;
  let iconAPI: IconAPI | null = null;
  let currentIcon: ProjectIconName = icon;

  // Root container
  const container = document.createElement("div");
  container.className = "projects";
  if (onClick) {
    container.style.cursor = "pointer";
    container.addEventListener("click", onClick);
  } else if (onChevronClick) {
    // Make whole component a touch target that opens the same modal anchored to the chevron
    container.style.cursor = "pointer";
    container.addEventListener("click", () => {
      const anchor = chevronAPI?.getElement() ?? chevronEl;
      onChevronClick(anchor);
    });
  }

  // Icon element
  const iconEl = document.createElement("div");
  iconEl.className = "projects__icon";

  const attachIcon = (): void => {
    iconEl.innerHTML = "";
    iconAPI?.destroy();
    iconAPI = createIcon({
      name: currentIcon,
      size: 20,
      color: "var(--color-medium-grey)",
      useContainer: true,
    });
    iconEl.appendChild(iconAPI.getElement());
  };

  // Name element
  const nameEl = document.createElement("div");
  nameEl.className = "projects__name";

  // Version badge (conditionally rendered)
  const versionEl = document.createElement("div");
  versionEl.className = "projects__version";

  // Chevron container
  const chevronEl = document.createElement("div");
  chevronEl.className = "projects__chevron";

  const attachChevron = (): void => {
    chevronAPI?.destroy();
    chevronAPI = createChevron({
      orientation: "up",
      className: "projects__chevron-container",
      svgClassName: "projects__chevron-svg",
      ...(onChevronClick
        ? {
            onClick: () => {
              const anchor = chevronAPI?.getElement() ?? chevronEl;
              onChevronClick(anchor);
            },
          }
        : {}),
    });
    chevronEl.appendChild(chevronAPI.getElement());
  };

  const updateName = (): void => {
    nameEl.textContent = currentProjectName;
  };

  const updateVersion = (): void => {
    const shouldShow = currentTotalVersions > 1 && !!currentVersionLabel;
    versionEl.style.display = shouldShow ? "inline-flex" : "none";
    if (shouldShow) {
      versionEl.textContent = String(currentVersionLabel);
    } else {
      versionEl.textContent = "";
    }
  };

  const render = (): void => {
    container.innerHTML = "";
    updateName();
    updateVersion();

    // Layout: [icon] [name] [version?] [chevron]
    attachIcon();
    container.appendChild(iconEl);
    container.appendChild(nameEl);
    container.appendChild(versionEl);
    attachChevron();
    container.appendChild(chevronEl);
  };

  const setProjectName = (name: string): void => {
    if (isDestroyed) return;
    currentProjectName = name;
    updateName();
  };

  const setVersion = (label: string | null, total?: number): void => {
    if (isDestroyed) return;
    currentVersionLabel = label;
    if (typeof total === "number") {
      currentTotalVersions = total;
    }
    updateVersion();
  };

  const destroy = (): void => {
    if (isDestroyed) return;
    isDestroyed = true;
    chevronAPI?.destroy();
    chevronAPI = null;
    iconAPI?.destroy();
    iconAPI = null;
    if (onClick) {
      container.removeEventListener("click", onClick);
    }
    container.remove();
  };

  const setIcon = (newIcon: ProjectIconName): void => {
    if (isDestroyed) return;
    currentIcon = newIcon;
    attachIcon();
  };

  // Init
  render();

  return {
    getElement: () => container,
    setProjectName,
    setVersion,
    setIcon,
    destroy,
  };
};

export const createProjectsInContainer = (
  container: HTMLElement,
  config: ProjectsConfig = {}
): ProjectsAPI => {
  const api = createProjects(config);
  container.appendChild(api.getElement());
  return api;
};
